import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { AuthProvider } from './lib/auth'
import { AdminPage } from './pages/Admin'
import { ApprovalsPage } from './pages/Approvals'
import { EngineeringPage } from './pages/Engineering'
import { EvaluationsPage } from './pages/Evaluations'
import { LearningPage } from './pages/Learning'
import { CommandCenterPage } from './pages/CommandCenter'
import { EnterprisePage } from './pages/Enterprise'
import { InterviewPage } from './pages/Interview'
import { InvitePage } from './pages/Invite'
import { resolveHostMode } from './lib/hostMode'
import { GatewayLanding } from './pages/GatewayLanding'
import { LandingPage } from './pages/Landing'
import { RalphPage } from './pages/Ralph'
import { SignInPage } from './pages/SignIn'
import { SignUpPage } from './pages/SignUp'
import { SkillsPage } from './pages/Skills'
import { EphemeralPage } from './pages/Ephemeral'
import { ProductPage } from './pages/Product'
import { ShopPage } from './pages/Shop'
import { TeamsPage } from './pages/Teams'
import { TicketsPage } from './pages/Tickets'
import { SquadsPage } from './pages/Squads'
import { CasesPage } from './pages/Cases'
import { WorkforcePage } from './pages/Workforce'

// afos360.com is normally served by the static marketing bundle (vite.marketing.config.ts).
// If the SPA is ever reached on the marketing host (or VITE_AFOS360_HOST_MODE=marketing in dev),
// render the same marketing site instead of the product shell; lazy so app users never load it.
const MarketingSite = lazy(async () => {
  await import('./marketing/marketing.css')
  const mod = await import('./marketing/MarketingApp')
  return { default: mod.MarketingApp }
})

export default function App() {
  const location = useLocation()
  const host = resolveHostMode()
  if (host.role === 'marketing') {
    return (
      <Suspense fallback={null}>
        <MarketingSite />
      </Suspense>
    )
  }
  // app.afos360.ai opens on the full-screen sign-in front door (signed-in users are sent
  // straight to the dashboard from there).
  const Home =
    host.role === 'app'
      ? SignInPage
      : host.role === 'gateway' || host.role === 'vanity'
        ? GatewayLanding
        : LandingPage
  return (
    <AuthProvider>
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/app" element={<CommandCenterPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/learning" element={<LearningPage />} />
            <Route path="/evaluations" element={<EvaluationsPage />} />
            <Route path="/engineering" element={<EngineeringPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/ralph" element={<RalphPage />} />
            <Route path="/interview" element={<InterviewPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/skills" element={<SkillsPage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/squads" element={<SquadsPage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/workforce" element={<WorkforcePage />} />
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/ephemeral" element={<EphemeralPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </AuthProvider>
  )
}
